// Shared date/time formatters for the web app.
//
// Rules (see also mobile/src/utils/datetime.ts — must stay in sync):
//   * Backend keeps YYYY-MM-DD for date-only fields and UTC ISO for timestamps.
//     Storage format is NEVER changed here — this file only affects display.
//   * User-facing display format is US: date-only fields render MM/DD/YYYY,
//     full timestamps render "MM/DD/YYYY h:mm AM/PM CT" in America/Chicago.
//   * Business decisions still run in America/Chicago; that never changes here.
//   * `formatBusinessDate` preserves the exact calendar day of a YYYY-MM-DD
//     value (parsed at local noon so no UTC shift can nudge it a day earlier),
//     and interprets a full ISO timestamp as a real point in time formatted
//     against the Chicago calendar so the displayed date is always CT-correct.
//   * `formatTimestamp` always presents timestamps in Central Time with a
//     literal "CT" suffix (no DST-varying CST/CDT).
//   * Empty/invalid inputs render as an em-dash.

const EN_US = "en-US";
const CHICAGO_TZ = "America/Chicago";
const DASH = "—";

const isDateOnly = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

export function formatBusinessDate(value) {
  if (value === null || value === undefined || value === "") return DASH;
  const s = String(value).trim();
  if (!s) return DASH;
  if (isDateOnly(s)) {
    const date = new Date(`${s}T12:00:00`);
    if (Number.isNaN(date.getTime())) return DASH;
    return date.toLocaleDateString(EN_US, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString(EN_US, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: CHICAGO_TZ,
  });
}

export function formatBusinessDates(value) {
  if (value === null || value === undefined || value === "") return DASH;
  return String(value)
    .split(" · ")
    .map((part) => formatBusinessDate(part))
    .join(" · ");
}

export function formatTimestamp(value) {
  if (value === null || value === undefined || value === "") return DASH;
  const s = String(value).trim();
  if (!s) return DASH;
  if (isDateOnly(s)) return formatBusinessDate(s);
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return DASH;
  const dateStr = date.toLocaleDateString(EN_US, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: CHICAGO_TZ,
  });
  const timeStr = date.toLocaleTimeString(EN_US, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: CHICAGO_TZ,
  });
  return `${dateStr} ${timeStr} CT`;
}

// Today's calendar date in Central Time as a YYYY-MM-DD string. Use this for
// business-day comparisons (e.g. `record.expires_on < todayInCentralTime()`),
// not for display.
export function todayInCentralTime() {
  return new Date().toLocaleDateString("en-CA", { timeZone: CHICAGO_TZ });
}

// Time-of-day only, in Central Time (e.g. "10:30 AM"). Callers must supply a
// full ISO timestamp; a bare YYYY-MM-DD has no meaningful time and returns "—".
export function formatTimeOfDay(value) {
  if (value === null || value === undefined || value === "") return DASH;
  const s = String(value).trim();
  if (!s || isDateOnly(s)) return DASH;
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleTimeString(EN_US, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: CHICAGO_TZ,
  });
}
